import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { crearVideoObject, videoDemo } from "@/lib/video-demo";

const canonical = `https://pymevisible.cl${videoDemo.path}`;
const title = `${videoDemo.name} | Pyme Visible`;

export const Route = createFileRoute("/videos/stand-nfc-resenas-google")({
  component: DemostracionPage,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: videoDemo.description },
      { property: "og:title", content: title },
      { property: "og:description", content: videoDemo.description },
      { property: "og:type", content: "video.other" },
      { property: "og:url", content: canonical },
      { property: "og:image", content: `https://pymevisible.cl${videoDemo.thumbnailPath}` },
      { property: "og:video", content: `https://pymevisible.cl${videoDemo.contentPath}` },
      { property: "og:video:type", content: "video/mp4" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://pymevisible.cl${videoDemo.thumbnailPath}` },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            crearVideoObject(),
            {
              "@type": "WebPage",
              "@id": `${canonical}#webpage`,
              url: canonical,
              name: title,
              description: videoDemo.description,
              mainEntity: { "@id": `${canonical}#video` },
              isPartOf: { "@id": "https://pymevisible.cl/#website" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Inicio",
                  item: "https://pymevisible.cl/",
                },
                { "@type": "ListItem", position: 2, name: "Demostración NFC", item: canonical },
              ],
            },
          ],
        }),
      },
    ],
  }),
});

function DemostracionPage() {
  return (
    <div className="min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main id="contenido">
        <article className="pv-page-hero border-b border-ink/10">
          <div className="mx-auto max-w-[1100px] px-5 py-5 sm:px-8 sm:py-8">
            <nav
              aria-label="Ruta de navegación"
              className="pv-breadcrumb inline-flex flex-wrap items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-slate"
            >
              <a href="/" className="hover:text-ink hover:underline">
                Inicio
              </a>
              <span aria-hidden="true">/</span>
              <span aria-current="page" className="text-ink">
                Demostración NFC
              </span>
            </nav>
            <h1 className="mx-auto mt-5 max-w-3xl text-center text-3xl font-extrabold leading-tight text-ink sm:text-5xl">
              {videoDemo.name}
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-slate sm:text-base">
              {videoDemo.description}
            </p>
            <figure className="pv-surface-card mx-auto mt-5 max-w-[560px] overflow-hidden rounded-[1.75rem] p-2 sm:p-3">
              <video
                src={videoDemo.contentPath}
                poster={videoDemo.thumbnailPath}
                width={1080}
                height={1080}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                disablePictureInPicture
                aria-label={videoDemo.description}
                className="aspect-square w-full rounded-[1.25rem] object-contain"
              >
                <a href={videoDemo.contentPath}>Ver la demostración del stand NFC.</a>
              </video>
              <figcaption className="px-2 pb-1 pt-3 text-center text-xs leading-relaxed text-slate">
                Video sin audio. La valoración la elige cada cliente según su experiencia.
              </figcaption>
            </figure>
            <div className="mx-auto mt-8 max-w-2xl pb-4">
              <h2 className="text-2xl font-bold text-ink">Lo que ocurre en la demostración</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate sm:text-base">
                El teléfono se acerca al chip NFC del stand, muestra la notificación del enlace y,
                al tocarla, abre el formulario de reseña de Google. El tramo de carga está acelerado
                para resumir la demostración; el tiempo real depende del teléfono y su conexión.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate sm:text-base">
                El stand también incluye un código QR como alternativa. Configuramos ambos accesos
                con el enlace del negocio antes de entregarlo. Facilita llegar al formulario y cada
                cliente decide libremente qué opinión y valoración publicar.
              </p>
              <a
                href="/stand-nfc-qr"
                className="pv-button pv-button--primary mt-5 inline-flex min-h-11 items-center gap-2 px-5 py-3 text-sm font-bold"
              >
                Ver modelos y precios <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
